import React from 'react';
import Container from './Container';
import Button from './Button';
import Reveal from './Reveal';
import { SHOPSTAR_GO, shopstarGoHref } from '../layout/siteData';

// Tinted check, sized to match the plan-card checks in Pricing.jsx.
const GoCheck = () => (
  <svg className="flex-none text-ink/70" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="12" fill="currentColor" opacity="0.14" />
    <path d="M7 12.5l3.2 3.2L17 8.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Compact Shopstar Go card for the pricing grid. It sits alongside the legacy
 * plan cards but is deliberately branded as a separate product (Go pink, Go
 * logo, "New platform" tag) so it reads as "a different option for different
 * needs" rather than a fourth plan to be fee-compared. We lead with "Free to
 * start" — Go's free tier is 6% per sale, which would lose a head-to-head next
 * to the legacy 0.05–1.7% transaction fees, so fees are intentionally not the
 * pitch here.
 */
export const ShopstarGoCard = () => (
  <Reveal
    data-testid="pricing-shopstar-go-card"
    className="relative flex flex-col rounded-4xl border border-ink/10 p-9 shadow-soft transition-all hover:-translate-y-1"
    style={{ backgroundColor: SHOPSTAR_GO.bg }}
  >
    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-ink px-4 py-1.5 text-[0.78rem] font-extrabold uppercase tracking-wide text-white">
      Sell anything
    </span>
    <img src={SHOPSTAR_GO.logo} alt={SHOPSTAR_GO.logoAlt} width="872" height="126" className="h-6 w-auto self-start object-contain" />
    <p className="mt-3 font-semibold text-ink/70">Sell more than physical</p>
    <div className="mt-6 flex items-baseline gap-2">
      <span className="text-[clamp(2.4rem,5vw,3rem)] font-extrabold leading-none tracking-tight text-ink">Free</span>
      <span className="font-bold text-ink/60">to start</span>
    </div>
    <p className="mt-2 text-sm text-ink/60">No monthly fee · no card needed</p>
    <p className="mt-1 text-xs text-ink/55" data-testid="pricing-shopstar-go-separate-note">{SHOPSTAR_GO.separateNote}</p>
    <div className="mt-6">
      <Button href={shopstarGoHref('pricing_card')} full variant="soft" data-testid="pricing-shopstar-go-card-cta">
        Explore Shopstar Go
      </Button>
    </div>
    <p className="mt-3 text-center text-sm text-ink/55">We only earn when you sell</p>
    <ul className="mt-7 flex flex-col gap-4">
      {SHOPSTAR_GO.productTypes.map((p) => (
        <li key={p.name} className="flex items-start gap-2.5">
          <span className="mt-0.5"><GoCheck /></span>
          <div>
            <span className="block font-semibold leading-tight text-ink/90">{p.name}</span>
            <span className="mt-0.5 block text-[0.8rem] leading-snug text-ink/55">{p.blurb}</span>
          </div>
        </li>
      ))}
    </ul>
  </Reveal>
);

/**
 * Full-width Shopstar Go cross-sell band. The fuller pitch lower down the
 * pricing page — for sellers who scroll past the grid and need bookings,
 * memberships, tickets or downloads. Mirrors the CtaBand shape (contained
 * rounded panel) but in Go pink so it clearly belongs to the new product.
 */
export const ShopstarGoBand = () => (
  <section className="bg-white py-14 md:py-20" data-testid="pricing-shopstar-go-band">
    <Container>
      <div
        className="relative overflow-hidden rounded-5xl border border-ink/10 px-6 py-14 md:px-12 md:py-16"
        style={{ backgroundColor: SHOPSTAR_GO.bg }}
      >
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <img src={SHOPSTAR_GO.logo} alt={SHOPSTAR_GO.logoAlt} width="872" height="126" className="h-7 w-auto self-start object-contain" />
            <h2 className="mt-5 text-h2 font-extrabold text-ink">Selling more than physical products?</h2>
            <p className="mt-4 max-w-[48ch] text-lead text-ink/75">
              Shopstar Go is the simplest way to sell products, downloads, bookings, memberships
              and tickets — all from one page. It’s free to start, with no monthly fee and no
              card required.
              {` ${SHOPSTAR_GO.separateNote} at shopstar.io.`}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
              <Button href={shopstarGoHref('pricing_band')} size="lg" variant="soft" data-testid="pricing-shopstar-go-band-cta">
                Explore Shopstar Go
              </Button>
              <span className="font-semibold text-ink/70">Free to start · only pay when you sell</span>
            </div>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {SHOPSTAR_GO.productTypes.map((p) => (
              <li key={p.name} className="flex items-center gap-2.5 rounded-2xl bg-white/70 px-4 py-3 font-bold text-ink">
                <GoCheck />{p.name}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Container>
  </section>
);
